import React, { useState, useEffect } from 'react';
import { toChileDate, fromChileInput } from '../../utils/chileTime';
import { useNavigate } from 'react-router-dom';
import { trainingsApi, coursesApi, companiesApi, trainersApi, enrollmentsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, MagnifyingGlass, Pencil, Eye, Chalkboard, Calendar as CalendarIcon, 
  Users, Clock, Play, CheckCircle, XCircle, Trash, ListBullets, CaretLeft, CaretRight
} from '@phosphor-icons/react';
import {
  format, isSameDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, addWeeks, subWeeks, addMonths, subMonths, addDays, subDays,
  isToday, isSameMonth, getHours, getMinutes, isWithinInterval, startOfDay, endOfDay
} from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import BulkDeleteConfirmDialog from '../../components/BulkDeleteConfirmDialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';

const TRAINING_TYPES = [
  'Certificación', 'Calificación', 'Legal',
  'Seguridad y Salud Ocupacional Específica de Área',
  'Seguridad y Salud Ocupacional General de la Empresa'
];
const MODALITIES = [
  'E-Learning', 'E-Learning Teórico-Práctico', 'Presencial',
  'Presencial Teórico-Práctico', 'Teórico-Práctico', 'Video'
];

const STATUS_CONFIG = {
  sin_conformar: { label: 'Sin Conformar', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20', dot: 'bg-yellow-500' },
  conformada: { label: 'Conformada', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', dot: 'bg-blue-500' },
  realizada: { label: 'Realizada', className: 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20', dot: 'bg-[#2BBAA4]' },
};

// ==================== CALENDAR VIEW ====================
function CalendarView({ trainings, currentDate, calendarView, onDateChange, onViewChange, navigate }) {
  const goTo = (dir) => {
    const fns = {
      week: dir === 'prev' ? subWeeks : addWeeks,
      month: dir === 'prev' ? subMonths : addMonths,
      day: dir === 'prev' ? subDays : addDays,
    };
    onDateChange(fns[calendarView](currentDate, 1));
  };

  const getTitle = () => {
    if (calendarView === 'week') {
      const s = startOfWeek(currentDate, { weekStartsOn: 1 });
      const e = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(s, "dd MMM", { locale: es })} - ${format(e, "dd MMM yyyy", { locale: es })}`;
    }
    if (calendarView === 'month') return format(currentDate, "MMMM yyyy", { locale: es });
    return format(currentDate, "EEEE dd 'de' MMMM yyyy", { locale: es });
  };

  const getTrainingsForDay = (day) => {
    return trainings.filter(t => t.scheduled_date && isSameDay(toChileDate(t.scheduled_date), day));
  };

  const renderTrainingBlock = (t) => {
    const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.sin_conformar;
    const time = t.scheduled_date ? format(toChileDate(t.scheduled_date), "HH:mm") : '';
    return (
      <div
        key={t.id}
        onClick={() => navigate(`/admin/trainings/${t.id}`)}
        className={`text-[10px] leading-tight px-1.5 py-1 rounded cursor-pointer transition-all hover:opacity-80 ${sc.className} border truncate`}
        title={`${time} - ${t.name}`}
        data-testid={`cal-event-${t.id}`}
      >
        <span className="font-semibold">{time}</span> {t.name}
      </div>
    );
  };

  // Week View
  const renderWeek = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(currentDate, { weekStartsOn: 1 }) });

    return (
      <div className="grid grid-cols-7 border-t border-l border-[#2B4B8C]/10">
        {days.map(day => {
          const dayTrainings = getTrainingsForDay(day);
          const today = isToday(day);
          return (
            <div key={day.toISOString()} className={`border-r border-b border-[#2B4B8C]/10 min-h-[120px] ${today ? 'bg-[#2BBAA4]/5' : ''}`}>
              <div className={`px-2 py-1 text-xs font-medium border-b border-[#2B4B8C]/5 flex items-center justify-between ${today ? 'text-[#2BBAA4]' : 'text-[#2B4B8C]/50'}`}>
                <span>{format(day, 'EEE', { locale: es })}</span>
                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${today ? 'bg-[#2BBAA4] text-white font-bold' : ''}`}>
                  {format(day, 'd')}
                </span>
              </div>
              <div className="p-1 space-y-0.5">
                {dayTrainings.map(renderTrainingBlock)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Month View
  const renderMonth = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: calStart, end: calEnd });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div>
        <div className="grid grid-cols-7 border-t border-l border-[#2B4B8C]/10">
          {['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'].map(d => (
            <div key={d} className="border-r border-b border-[#2B4B8C]/10 px-2 py-1.5 text-xs font-semibold text-[#2B4B8C]/50 bg-[#f8fafc] text-center">{d}</div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-l border-[#2B4B8C]/10">
            {week.map(day => {
              const dayTrainings = getTrainingsForDay(day);
              const today = isToday(day);
              const inMonth = isSameMonth(day, currentDate);
              return (
                <div key={day.toISOString()} className={`border-r border-b border-[#2B4B8C]/10 min-h-[90px] ${!inMonth ? 'opacity-30' : ''} ${today ? 'bg-[#2BBAA4]/5' : ''}`}>
                  <div className="px-1.5 py-0.5 text-right">
                    <span className={`text-xs inline-flex w-6 h-6 items-center justify-center rounded-full ${today ? 'bg-[#2BBAA4] text-white font-bold' : 'text-[#2B4B8C]/50'}`}>
                      {format(day, 'd')}
                    </span>
                  </div>
                  <div className="px-1 pb-1 space-y-0.5">
                    {dayTrainings.slice(0, 3).map(renderTrainingBlock)}
                    {dayTrainings.length > 3 && (
                      <p className="text-[9px] text-[#2B4B8C]/40 text-center">+{dayTrainings.length - 3} mas</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Day View
  const renderDay = () => {
    const dayTrainings = getTrainingsForDay(currentDate);
    const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 7:00 to 20:00

    return (
      <div className="border border-[#2B4B8C]/10 rounded-lg overflow-hidden">
        <div className={`px-4 py-2 text-sm font-medium border-b border-[#2B4B8C]/10 ${isToday(currentDate) ? 'bg-[#2BBAA4]/5 text-[#2BBAA4]' : 'bg-[#f8fafc] text-[#2B4B8C]/60'}`}>
          {format(currentDate, "EEEE dd 'de' MMMM", { locale: es })}
          {isToday(currentDate) && <Badge className="ml-2 bg-[#2BBAA4]/10 text-[#2BBAA4] border-0 text-[10px]">Hoy</Badge>}
        </div>
        {hours.map(h => {
          const hourTrainings = dayTrainings.filter(t => {
            const d = toChileDate(t.scheduled_date);
            return getHours(d) === h;
          });
          return (
            <div key={h} className="flex border-b border-[#2B4B8C]/5 min-h-[48px]">
              <div className="w-16 shrink-0 px-2 py-1 text-xs text-[#2B4B8C]/35 font-mono text-right border-r border-[#2B4B8C]/5 bg-[#fafbfc]">
                {String(h).padStart(2, '0')}:00
              </div>
              <div className="flex-1 p-1 space-y-0.5">
                {hourTrainings.map(t => {
                  const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.sin_conformar;
                  return (
                    <div
                      key={t.id}
                      onClick={() => navigate(`/admin/trainings/${t.id}`)}
                      className={`text-xs px-2 py-1.5 rounded cursor-pointer hover:opacity-80 ${sc.className} border`}
                      data-testid={`cal-day-event-${t.id}`}
                    >
                      <span className="font-semibold">{format(toChileDate(t.scheduled_date), "HH:mm")}</span>
                      <span className="ml-1.5">{t.name}</span>
                      <span className="ml-1.5 text-[10px] opacity-60">{t.enrolled_count}/{t.max_participants}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Card className="bg-white border-[#2B4B8C]/10">
      <CardContent className="p-4">
        {/* Calendar Nav */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => goTo('prev')} className="h-8 w-8 p-0 text-[#2B4B8C]/50" data-testid="cal-prev">
              <CaretLeft size={18} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => goTo('next')} className="h-8 w-8 p-0 text-[#2B4B8C]/50" data-testid="cal-next">
              <CaretRight size={18} />
            </Button>
            <h3 className="text-sm font-semibold text-[#2B4B8C] capitalize">{getTitle()}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onDateChange(new Date())} className="h-7 text-xs border-[#2B4B8C]/10 text-[#2B4B8C]/60" data-testid="cal-today">
              Hoy
            </Button>
            <div className="flex bg-[#2B4B8C]/5 rounded-md p-0.5">
              {[{ v: 'day', l: 'Dia' }, { v: 'week', l: 'Semana' }, { v: 'month', l: 'Mes' }].map(o => (
                <button
                  key={o.v}
                  onClick={() => onViewChange(o.v)}
                  className={`px-2.5 py-1 text-xs rounded font-medium transition-all ${calendarView === o.v ? 'bg-white text-[#2B4B8C] shadow-sm' : 'text-[#2B4B8C]/50 hover:text-[#2B4B8C]/70'}`}
                  data-testid={`cal-view-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-3 text-[10px]">
          {Object.entries(STATUS_CONFIG).map(([key, sc]) => (
            <div key={key} className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
              <span className="text-[#2B4B8C]/50">{sc.label}</span>
            </div>
          ))}
        </div>

        {calendarView === 'week' && renderWeek()}
        {calendarView === 'month' && renderMonth()}
        {calendarView === 'day' && renderDay()}
      </CardContent>
    </Card>
  );
}

// ==================== MAIN PAGE ====================
export default function TrainingsPage() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deletingTraining, setDeletingTraining] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [calendarView, setCalendarView] = useState('week');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarTrainings, setCalendarTrainings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ITEMS_PER_PAGE = 10;
  const [formData, setFormData] = useState({
    name: '', description: '', training_type: '', modality: '',
    duration_minutes: 60, max_participants: 30, reserved_spots: 0, scheduled_date: '',
    connection_url: '', passing_percentage: 70, max_evaluation_attempts: 3,
    evaluation_time_minutes: 30, questions_count: 10, course_id: '', client_id: '', trainer_ids: []
  });
  const [formMandante, setFormMandante] = useState('');

  // Mandantes derived from the courses that have an owner
  const courseMandantes = [...new Map(
    courses.filter(c => c.client_id).map(c => [c.client_id, c.client_name || 'Sin mandante'])
  ).entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));

  // Default the modal mandante filter to Albemarle once courses are loaded
  useEffect(() => {
    if (courses.length && !formMandante) {
      const alb = courseMandantes.find(m => m.name.trim().toUpperCase() === 'ALBEMARLE')
        || courseMandantes.find(m => m.name.toUpperCase().includes('ALBEMARLE'));
      if (alb) setFormMandante(alb.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  // Load dropdowns once
  useEffect(() => {
    Promise.all([
      coursesApi.getAll(), companiesApi.getAll({ is_client: true, page_size: 999 }), trainersApi.getAll()
    ]).then(([coursesRes, companiesRes, trainersRes]) => {
      setCourses(coursesRes.data);
      const allComps = companiesRes.data.items || companiesRes.data;
      setCompanies(allComps.filter(c => c.is_client));
      setTrainers(trainersRes.data);
    }).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Fetch trainings from server (paginated)
  useEffect(() => { fetchData(); }, [currentPage, searchDebounced, statusFilter, clientFilter, dateFilter]);

  // Load calendar data when switching to calendar mode
  useEffect(() => {
    if (viewMode === 'calendar') {
      trainingsApi.getAll({ page_size: 5000 }).then(res => {
        setCalendarTrainings(res.data.items || res.data);
      }).catch(() => {});
    }
  }, [viewMode]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [searchDebounced, statusFilter, clientFilter, dateFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: ITEMS_PER_PAGE };
      if (searchDebounced) params.search = searchDebounced;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (clientFilter !== 'all') params.client_id = clientFilter;
      if (dateFilter) params.date = format(dateFilter, 'yyyy-MM-dd');
      const res = await trainingsApi.getAll(params);
      const data = res.data;
      setTrainings(data.items || data);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total || 0);
    } catch { } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, scheduled_date: fromChileInput(formData.scheduled_date) };
      if (!data.course_id) delete data.course_id;
      if (!data.client_id) delete data.client_id;
      await trainingsApi.create(data);
      toast.success('Capacitacion creada');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', training_type: '', modality: '',
      duration_minutes: 60, max_participants: 30, reserved_spots: 0, scheduled_date: '',
      connection_url: '', passing_percentage: 70, max_evaluation_attempts: 3,
      evaluation_time_minutes: 30, questions_count: 10, course_id: '', client_id: '', trainer_ids: []
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    const allSel = trainings.every(t => selectedIds.has(t.id));
    setSelectedIds(prev => { const n = new Set(prev); trainings.forEach(t => allSel ? n.delete(t.id) : n.add(t.id)); return n; });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const getStatusBadge = (status) => {
    const c = STATUS_CONFIG[status] || STATUS_CONFIG.sin_conformar;
    return <Badge className={`${c.className} border`}>{c.label}</Badge>;
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trainings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Capacitaciones</h1>
          <p className="text-[#2B4B8C]/60">Gestiona el ciclo de capacitaciones</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-[#2B4B8C]/5 rounded-lg p-0.5">
            <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${viewMode === 'table' ? 'bg-white text-[#2B4B8C] shadow-sm' : 'text-[#2B4B8C]/50'}`} data-testid="view-table-btn">
              <ListBullets size={16} /> Lista
            </button>
            <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all ${viewMode === 'calendar' ? 'bg-white text-[#2B4B8C] shadow-sm' : 'text-[#2B4B8C]/50'}`} data-testid="view-calendar-btn">
              <CalendarIcon size={16} /> Calendario
            </button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="create-training-btn">
                <Plus className="mr-2" size={20} /> Nueva Capacitacion
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#2B4B8C]/10 w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader>
                <DialogTitle className="text-[#2B4B8C]">Nueva Capacitacion</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 min-w-0">
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Mandante</Label>
                  <Select value={formMandante} onValueChange={(v) => {
                    setFormMandante(v);
                    const current = courses.find(c => c.id === formData.course_id);
                    if (current && current.client_id !== v) {
                      setFormData(fd => ({ ...fd, course_id: '', name: '', training_type: '', description: '', client_id: v }));
                    }
                  }}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="select-training-mandante">
                      <SelectValue placeholder="Seleccionar mandante" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64">
                      {courseMandantes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#2B4B8C]/80">Curso *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => {
                    const course = courses.find(c => c.id === v);
                    if (course) {
                      setFormData({
                        ...formData, course_id: v, name: course.name,
                        description: course.description || '', training_type: course.training_type || '',
                        duration_minutes: course.default_duration_minutes || 60,
                        passing_percentage: course.default_passing_percentage || 70,
                        client_id: course.client_id || ''
                      });
                    }
                  }}>
                    <SelectTrigger className="w-full min-w-0 max-w-full bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C] [&>span]:min-w-0 [&>span]:truncate" data-testid="select-course">
                      <SelectValue placeholder="Seleccionar curso" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-64 max-w-[var(--radix-select-trigger-width)]">
                      {courses.filter(c => !formMandante || c.client_id === formMandante).map(c => (<SelectItem key={c.id} value={c.id} className="[&>span:last-child]:truncate">{c.name} - {c.training_type}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.course_id && (
                  <>
                    <div className="p-3 rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/15 text-sm text-[#2B4B8C]/70 space-y-1">
                      <p><strong className="text-[#2B4B8C]">Tipo:</strong> {formData.training_type}</p>
                      <p><strong className="text-[#2B4B8C]">Duracion:</strong> {formData.duration_minutes} min &middot; <strong className="text-[#2B4B8C]">Aprobacion:</strong> {formData.passing_percentage}%</p>
                      {formData.description && <p><strong className="text-[#2B4B8C]">Descripcion:</strong> {formData.description}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2B4B8C]/80">Modalidad</Label>
                      <Select value={formData.modality} onValueChange={(v) => setFormData({ ...formData, modality: v })}>
                        <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-white border-[#2B4B8C]/10">
                          {MODALITIES.map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#2B4B8C]/80">Fecha y hora *</Label>
                        <Input type="datetime-local" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="scheduled-date" required />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#2B4B8C]/80">Max participantes</Label>
                        <Input type="number" value={formData.max_participants} onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 30 })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="max-participants" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#2B4B8C]/80">Cupos reservados (admin)</Label>
                        <Input type="number" min="0" max={formData.max_participants - 1} value={formData.reserved_spots} onChange={(e) => setFormData({ ...formData, reserved_spots: Math.min(parseInt(e.target.value) || 0, formData.max_participants - 1) })} className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="reserved-spots" />
                        {formData.reserved_spots > 0 && (
                          <p className="text-[10px] text-[#2B4B8C]/50">
                            {formData.reserved_spots} reservados + {formData.max_participants - formData.reserved_spots} abiertos = {formData.max_participants} total
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#2B4B8C]/80">URL de conexion (e-learning)</Label>
                        <Input value={formData.connection_url} onChange={(e) => setFormData({ ...formData, connection_url: e.target.value })} placeholder="https://meet.google.com/..." className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
                      </div>
                    </div>
                  </>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="border-[#2B4B8C]/10">Cancelar</Button>
                  <Button type="submit" className="bg-[#2BBAA4] text-black hover:bg-[#239986]" disabled={!formData.course_id || !formData.scheduled_date} data-testid="submit-training-btn">Crear Capacitacion</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters (only in table mode) */}
      {viewMode === 'table' && (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
                <Input placeholder="Buscar por nombre o codigo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="status-filter"><SelectValue placeholder="Filtrar por estado" /></SelectTrigger>
                <SelectContent className="bg-white border-[#2B4B8C]/10">
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="sin_conformar">Sin Conformar</SelectItem>
                  <SelectItem value="conformada">Conformada</SelectItem>
                  <SelectItem value="realizada">Realizada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="w-full sm:w-52 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="client-filter"><SelectValue placeholder="Filtrar por mandante" /></SelectTrigger>
                <SelectContent className="bg-white border-[#2B4B8C]/10">
                  <SelectItem value="all">Todos los mandantes</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full sm:w-52 justify-start text-left font-normal bg-[#f8fafc] border-[#2B4B8C]/10 ${dateFilter ? 'text-[#2B4B8C]' : 'text-[#2B4B8C]/50'}`} data-testid="date-filter-btn">
                    <CalendarIcon size={16} className="mr-2 shrink-0" />
                    {dateFilter ? format(dateFilter, "dd 'de' MMMM yyyy", { locale: es }) : 'Filtrar por fecha'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#2B4B8C]/10" align="end">
                  <Calendar mode="single" selected={dateFilter} onSelect={setDateFilter} locale={es} className="rounded-md" />
                </PopoverContent>
              </Popover>
              {dateFilter && (
                <Button variant="ghost" size="sm" onClick={() => setDateFilter(null)} className="text-red-400 hover:text-red-500 shrink-0" data-testid="clear-date-filter">
                  <XCircle size={18} />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Delete Bar */}
      {viewMode === 'table' && selectedIds.size > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 animate-fade-in" data-testid="bulk-delete-bar-trainings">
          <span className="text-sm font-medium text-red-700">{selectedIds.size} capacitacion(es) seleccionada(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="clear-selection-trainings-btn">Deseleccionar</Button>
            <Button size="sm" onClick={() => setShowBulkDelete(true)} className="bg-red-500 text-white hover:bg-red-600 h-8" data-testid="bulk-delete-trainings-btn">
              <Trash size={14} className="mr-1" /> Eliminar Seleccionadas
            </Button>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView
          trainings={calendarTrainings}
          currentDate={calendarDate}
          calendarView={calendarView}
          onDateChange={setCalendarDate}
          onViewChange={setCalendarView}
          navigate={navigate}
        />
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card className="bg-white border-[#2B4B8C]/10">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                  <TableHead className="w-10">
                    <Checkbox checked={trainings.length > 0 && trainings.every(t => selectedIds.has(t.id))} onCheckedChange={toggleSelectAll} data-testid="select-all-trainings-checkbox" />
                  </TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Capacitacion</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Codigo</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Fecha Realizacion</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Fecha Vencimiento</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Inscritos</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trainings.map((training) => (
                  <TableRow key={training.id} className={`border-[#2B4B8C]/5 hover:bg-[#f8fafc] ${selectedIds.has(training.id) ? 'bg-red-50/50' : ''}`}>
                    <TableCell>
                      <Checkbox checked={selectedIds.has(training.id)} onCheckedChange={() => toggleSelect(training.id)} data-testid={`select-training-${training.id}`} />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-[#2B4B8C] font-medium">{training.name}</p>
                        <p className="text-xs text-[#2B4B8C]/50">{training.training_type}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#2B4B8C]/60 font-mono text-sm">{training.code}</TableCell>
                    <TableCell className="text-[#2B4B8C]/60">
                      {format(toChileDate(training.scheduled_date), "dd MMM yyyy HH:mm", { locale: es })}
                    </TableCell>
                    <TableCell className="text-[#2B4B8C]/60">
                      {(() => {
                        const course = courses.find(c => c.id === training.course_id);
                        if (course?.validity_years) {
                          const d = toChileDate(training.scheduled_date);
                          d.setFullYear(d.getFullYear() + course.validity_years);
                          const isExpired = d < new Date();
                          return (<span className={isExpired ? 'text-red-500 font-medium' : ''}>{format(d, "dd MMM yyyy", { locale: es })}{isExpired && ' (Vencido)'}</span>);
                        }
                        return '-';
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-[#2B4B8C]/60"><Users size={16} /><span>{training.enrolled_count}/{training.max_participants}</span></div>
                    </TableCell>
                    <TableCell>{getStatusBadge(training.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/trainings/${training.id}`)} className="border-[#2BBAA4]/30 text-[#2BBAA4] hover:bg-[#2BBAA4]/10 text-xs" data-testid={`detail-training-${training.id}`}>
                          <Eye size={14} className="mr-1" /> Detalle
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/trainings/${training.id}`)} className="text-[#2B4B8C]/60 hover:text-[#2B4B8C]" data-testid={`edit-training-${training.id}`}>
                          <Pencil size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeletingTraining(training)} className="text-red-400 hover:text-red-500" data-testid={`delete-training-${training.id}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {trainings.length === 0 && !loading && (
              <div className="text-center py-12 text-[#2B4B8C]/50">No se encontraron capacitaciones</div>
            )}
          </CardContent>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-[#2B4B8C]/10">
              <span className="text-xs text-[#2B4B8C]/50">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} className="h-8 text-xs border-[#2B4B8C]/10">Anterior</Button>
                <span className="text-xs text-[#2B4B8C]/60">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} className="h-8 text-xs border-[#2B4B8C]/10">Siguiente</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <DeleteConfirmDialog
        open={!!deletingTraining}
        onOpenChange={(open) => { if (!open) setDeletingTraining(null); }}
        entityName="Capacitacion"
        itemName={deletingTraining?.name || ''}
        fetchDependencies={deletingTraining ? () => trainingsApi.getDependencies(deletingTraining.id) : null}
        onConfirmDelete={async () => {
          await trainingsApi.delete(deletingTraining.id);
          toast.success('Capacitacion eliminada');
          setDeletingTraining(null);
          fetchData();
        }}
      />

      <BulkDeleteConfirmDialog
        open={showBulkDelete}
        onOpenChange={setShowBulkDelete}
        entityName="capacitacion(es)"
        count={selectedIds.size}
        fetchDependencies={() => trainingsApi.bulkDependencies([...selectedIds])}
        onConfirmDelete={async () => {
          await trainingsApi.bulkDelete([...selectedIds]);
          toast.success(`${selectedIds.size} capacitacion(es) eliminada(s)`);
          clearSelection();
          fetchData();
        }}
      />
    </div>
  );
}
